'use client';

import type { ComponentProps } from 'react';

import { Tabs as TabsPrimitive } from '@base-ui-components/react/tabs';

/**
 * A tab list and the panel it drives, unstyled.
 *
 * Pass-throughs over Base UI, like the collapsible beside them: the primitive
 * owns `role="tablist"` / `role="tab"` / `role="tabpanel"`, roving focus with
 * arrow keys, `aria-selected`, and the id pair that ties a tab to its panel.
 * What is added here is the `data-slot` every part of this tier carries, so a
 * section painting these can be reached by anatomy rather than by tag.
 *
 * Which tab is current is the caller's — `value` / `onValueChange` for a
 * controlled set, `defaultValue` for one that keeps its own. Base UI marks the
 * current tab `data-active`; a section paints off that mark rather than being
 * told which one it is.
 *
 * A panel whose tab is not current is NOT rendered, which is what keeps a set
 * of four panels from mounting four subtrees to show one. A caller that needs
 * every panel in the document says `keepMounted`.
 */

export const Tabs = (props: ComponentProps<typeof TabsPrimitive.Root>) => (
  <TabsPrimitive.Root data-slot="tabs" {...props} />
);

export const TabsList = (props: ComponentProps<typeof TabsPrimitive.List>) => (
  <TabsPrimitive.List data-slot="tabs-list" {...props} />
);

export const TabsTab = (props: ComponentProps<typeof TabsPrimitive.Tab>) => (
  <TabsPrimitive.Tab data-slot="tabs-tab" {...props} />
);

// Base UI calls it Panel (Radix's Content). Kept under the Base UI name rather
// than the shadcn-familiar `TabsContent`, because unlike the collapsible there
// is no `Content` in this family for it to be confused with.
export const TabsPanel = (props: ComponentProps<typeof TabsPrimitive.Panel>) => (
  <TabsPrimitive.Panel data-slot="tabs-panel" {...props} />
);
